export type FsmConnectEndInput = {
  fromNodeId: string | null | undefined;
  droppedNodeId: string | null | undefined;
  connectionIsValid: boolean | null;
};

export type FsmConnectPair = { source: string; target: string };

/**
 * Recover a self-transition from a React Flow connect-end that RF itself
 * rejected.
 *
 * Loose mode still treats a drop on the same node as invalid when the source
 * and target handles share an id (ours used to have none, and even with
 * distinct ids the full-node magnet often hits the handle the drag started
 * from). `onConnect` never fires; the rubber-band still showed, so it looked
 * like the add worked. If the pointer is still over the origin state, treat
 * that drop as source === target.
 *
 * When RF already accepted the connection, return null so `onConnect` is the
 * only caller.
 */
export function transitionFromConnectEnd(input: FsmConnectEndInput): FsmConnectPair | null {
  if (input.connectionIsValid) return null;
  const fromNodeId = input.fromNodeId;
  const droppedNodeId = input.droppedNodeId;
  if (fromNodeId == null || droppedNodeId == null) return null;
  if (fromNodeId !== droppedNodeId) return null;
  return { source: fromNodeId, target: droppedNodeId };
}

/** Client coordinates of a mouse or touch connect-end event. */
export function clientPointFromConnectEnd(
  event: MouseEvent | TouchEvent
): { x: number; y: number } | null {
  if ('changedTouches' in event) {
    const touch = event.changedTouches[0];
    if (!touch) return null;
    return { x: touch.clientX, y: touch.clientY };
  }
  return { x: event.clientX, y: event.clientY };
}

/** The React Flow node under the pointer, or null. */
export function nodeIdFromClientPoint(clientX: number, clientY: number): string | null {
  const el = document.elementFromPoint(clientX, clientY);
  if (el == null || typeof el.closest !== 'function') return null;
  const node = el.closest('.react-flow__node');
  return node?.getAttribute('data-id') ?? null;
}
